import { ApprButton } from '@/common/buttons/ApprButton';
import { ApprRouteProgressButton } from '@/common/buttons/ApprRouteProgressButton';
import { ExportButton } from '@/common/buttons/ExportButton';
import { ValButton } from '@/common/buttons/ValButton';
import { useEntityChangeSubscr } from '@/common/entityChange/EntityChangeContext';
import { useEntityFromRoute } from '@/common/hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { ContentBox } from '@/common/layout/ContentBox';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { useEffect, useState } from 'react';
import { useNavState } from '../../navState/NavStateContext';
import { EntityBanner } from '../entityBanner/EntityBanner';
import { EntityForm } from '../entityForm/EntityForm';
import { EntityProvider } from '../EntityProvider';
import { EntityTabs } from '../entityTabs/EntityTabs';
import { AsOfDate } from '../entityTitle/AsOfDate';
import { EntityEditButton } from '../entityTitle/EntityEditButton';
import { EntityTitle } from '../entityTitle/EntityTitle';
import { HistToggle } from '../entityTitle/HistToggle';
import { useEntityMgr } from '../entityView/EntityMgrContext';
import { EntityFormMode } from '../enumerations/EntityFormMode';

export const EntityDetail = ({ tabSpecs, augButtons, children }) => {
  const { navState } = useNavState();
  const { hist, listComponent, entityObj, setEntityObj, asOfDate, entityFormMode, entityDef } = useEntityMgr();
  const rsrc = useRsrc();

  const [exportReq, setExportReq] = useState();

  const module = useModuleFromRoute();
  const entity = useEntityFromRoute();

  const { refreshTrigger } = useEntityChangeSubscr(module, entity, ExprUtils.whereParams('id', navState.entityId));

  const [entityObjLoaded, setEntityObjLoaded] = useState(false);
  useEffect(() => {
    if (entityFormMode === EntityFormMode.ADD) {
      if (navState.entityObj) {
        setEntityObj(navState.entityObj);
      }

      setEntityObjLoaded(true);
      return;
    }

    const rowsReq = {};

    const exprs = [ExprUtils.whereParams('id', navState.entityId)];

    // As Of Date
    if (asOfDate) {
      exprs.push(ExprUtils.fieldExpr('verStartInstant', 'IS_LESS', asOfDate));
      exprs.push(ExprUtils.fieldExpr('verEndInstant', 'IS_GREATER_OR_EQUAL', asOfDate));
      rowsReq.hist = true;
    }

    rowsReq.expr = ExprUtils.and(...exprs);

    // TODO Review logic - temporary fix was renaming entityObj to responseEntityObj
    rsrc.one(module, entity, rowsReq).then((responseEntityObj) => {
      responseEntityObj ??= {}; // TODO: What to do if asOfDate is before Entity's creation?
      setEntityObj(responseEntityObj);
      setEntityObjLoaded(true);
      buildExportReq(rowsReq, responseEntityObj);
    });
  }, [refreshTrigger, asOfDate, entityFormMode]);

  let buildExportReq = (rowsReq, responseEntityObj) => {
    if (entityDef == null || rowsReq == null) return null;
    setExportReq({
      timeZoneName: DateUtils.timeZoneName(),
      displayName: entityDef.displayName,
      module: module,
      entity: entity,
      entityId: responseEntityObj.id,
      label: 'Info',
      rowsReq: rowsReq,
      uiType: 'DETAIL',
    });
  };

  // Don't load until entityObj is populated
  if (!entityObjLoaded) {
    return null;
  }

  /* Define the default buttons, and augment if necessary */
  let buttons = [];
  if (entityFormMode !== EntityFormMode.ADD) {
    buttons.push(<AsOfDate key="asOfDate" />, <HistToggle key="histToggle" />);
  }
  if (
    entityFormMode !== EntityFormMode.ADD &&
    (!(entityDef.validatable || entityDef.approvable) || ['DRAFT'].includes(entityObj.status))
  ) {
    buttons.push(<EntityEditButton key="entityEditButton" />);
  }
  if (entityDef.validatable && ['DRAFT', 'ACTIVE', 'ERROR'].includes(entityObj.status)) {
    buttons.push(<ValButton key="valButton" />);
  }
  if (entityDef.approvable && entityObj.status === 'DRAFT') {
    buttons.push(<ApprButton key="apprButton" />);
  }
  if (entityDef.approvable && entityObj.apprJobId) {
    buttons.push(<ApprRouteProgressButton key="apprRouteProgressButton" />);
  }
  if (augButtons) {
    augButtons(buttons);
  }
  if (!entityFormMode || entityFormMode !== EntityFormMode.ADD) {
    buttons = [
      ...buttons,
      <ExportButton key="export" entityName={StringUtils.toSingular(entityDef?.displayName)} exportReq={exportReq} />,
    ];
  }

  const FormComponent = EntityProvider.form(entity) ?? EntityForm;
  const defaultContent = tabSpecs ? <EntityTabs tabSpecs={tabSpecs} /> : <FormComponent />;

  return (
    <ContentBox>
      <EntityTitle buttons={buttons} />
      <EntityBanner />
      {hist ? listComponent : (children ?? defaultContent)}
    </ContentBox>
  );
};
